import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Ikon,
  Iskelet,
  Kart,
  SatirDugmesi,
  SatirMetni,
  Secim,
} from "@koclukpanel/ui";
import { ApiHatasi, benGetir, ogrenciListesi, type OgrenciSatiri } from "./api";

/** Son kullanılanlarda tutulan öğrenci sayısı — F-STV-16. */
const SON_KULLANILAN_SAYISI = 5;

/**
 * ================================================================
 * SON KULLANILANLAR TARAYICIDA, SUNUCUDA DEĞİL — F-STV-16
 * ================================================================
 * Şartnamenin gerekçesi: sunucuya yazılsaydı "kimin kime baktığı"
 * verisi üretilmiş olurdu ve denetim kaydının DIŞINDA ikinci bir
 * izleme kaynağı doğardı. Denetim kaydı zaten neyin okunduğunu
 * tutuyor; ikinci bir liste tutmanın tek işlevi, o listeyi okuyacak
 * birine yeni bir yetenek vermek olurdu.
 *
 * ANAHTAR KULLANICIYA BAĞLI. Ortak bilgisayarda iki uzman aynı
 * tarayıcıyı kullanınca listelerin karışması, tam olarak güvenilir
 * cihazda kaçındığımız şeyin bu ekrandaki hâli: kişiye ait bir şey
 * cihaza bağlanamaz.
 *
 * `try/catch` şart: gizli sekmede ve site verisi kapalı tarayıcıda
 * `localStorage` erişimin KENDİSİ fırlatıyor. Hatırlamamak özelliğin
 * kaybı, çökmesi ekranın kaybı.
 */
function anahtar(kullaniciId: string): string {
  return `kp_son_ogrenciler:${kullaniciId}`;
}

interface SonOgrenci {
  id: string;
  ad: string;
}

function sonlariOku(kullaniciId: string | null): SonOgrenci[] {
  if (!kullaniciId) return [];
  try {
    const ham = localStorage.getItem(anahtar(kullaniciId));
    if (!ham) return [];
    const cozulen: unknown = JSON.parse(ham);
    if (!Array.isArray(cozulen)) return [];
    return cozulen
      .filter(
        (o): o is SonOgrenci =>
          typeof o === "object" && o !== null && typeof (o as SonOgrenci).id === "string",
      )
      .slice(0, SON_KULLANILAN_SAYISI);
  } catch {
    return [];
  }
}

/**
 * Son kullanılanlara bir öğrenci yazar — F-STV-16.
 *
 * ================================================================
 * DIŞA VERİLİYOR: BAĞLAM YALNIZCA BU EKRANDA KURULMUYOR
 * ================================================================
 * Uzmanın günlük yolu iş listesi; oradan öğrenciye geçmek de bağlam
 * kurmaktır. Yazmayı yalnızca bu seçiciye bağlamak, özelliği iş
 * listesinden gelen kullanıcı için hiç çalışmaz hâlde bırakırdı.
 *
 * Çağıran, öğrencinin ekranı GERÇEKTEN açıldığında çağırmalı; seçip
 * hemen vazgeçen kullanıcı listeyi kirletmemeli.
 */
export function sonOgrenciyiYaz(
  kullaniciId: string | null,
  ogrenci: { id: string; ad: string },
): void {
  sonlariYaz(kullaniciId, ogrenci);
}

function sonlariYaz(kullaniciId: string | null, ogrenci: SonOgrenci): void {
  if (!kullaniciId) return;
  try {
    const yeni = [ogrenci, ...sonlariOku(kullaniciId).filter((o) => o.id !== ogrenci.id)].slice(
      0,
      SON_KULLANILAN_SAYISI,
    );
    localStorage.setItem(anahtar(kullaniciId), JSON.stringify(yeni));
  } catch {
    // Yazamamak sessizce geçiliyor: kullanıcının başlattığı bir iş
    // değil ve başarısızlığı onun yapacağı hiçbir şeyi değiştirmiyor.
  }
}

/**
 * ÖĞRENCİ SEÇİCİ — dört çıkmaz sokağın ortak çözümü
 *
 * ================================================================
 * MENÜDEN GİRİLEN EKRAN SENİ BAŞKA EKRANA YOLLAMAMALI
 * ================================================================
 * 06.08.2026'da canlı ölçüldü: Haftalık program · Denemeler · Veli
 * raporları · Abonelik — dördü de menüden açılınca tek bir şey
 * gösteriyordu:
 *
 *   "Önce öğrenci seçin"  +  [İş listesine git]
 *
 * Öğrenci seçmenin yolu O EKRANDA yoktu. Kullanıcı menüden bir yere
 * giriyor, ekran onu başka bir yere yolluyor ve oradan geri dönmesi
 * gerekiyordu. Dördü de menünün dörtte birini oluşturuyor.
 *
 * ================================================================
 * "İŞ LİSTESİNE GİT" YOLU KALDIRILMADI
 * ================================================================
 * F-ANL-09 iş listesinden DOĞRUDAN işe geçmeyi istiyor ve o yol daha
 * hızlı: bekleyen işi olan öğrenciyi zaten sıralı gösteriyor. Bu
 * seçici onun YERİNE değil, YANINA konuldu — menüden gelen kişi için.
 */
export function OgrenciSecici({
  baslik,
  aciklama,
  onSec,
  onIsListesi,
}: {
  baslik: string;
  /** Bu ekranda öğrenci seçmenin ne işe yaradığı */
  aciklama: string;
  onSec: (id: string, ad: string) => void;
  onIsListesi?: () => void;
}) {
  const [arama, setArama] = useState("");

  const liste = useQuery({
    queryKey: ["ogrenciler"],
    queryFn: ogrenciListesi,
    retry: false,
  });

  /*
    KULLANICI KİMLİĞİ PROP DEĞİL, ÖNBELLEKTEN.

    Prop yapmak `Uygulama.tsx`teki ON BİR çağrı yerini değiştirmeyi
    gerektirirdi ve o dosya başka bir oturumun şeridinde. Aynı sorgu
    anahtarı (`["ben"]`) kullanılınca yanıt zaten önbellekte: ek istek
    gitmiyor, kimsenin dosyasına dokunmak gerekmiyor.
  */
  const ben = useQuery({ queryKey: ["ben"], queryFn: benGetir, retry: false });
  const kullaniciId = ben.data?.userId ?? null;

  const [sonlar, setSonlar] = useState<SonOgrenci[]>(() => sonlariOku(kullaniciId));

  // Kimlik önbellekten SONRA gelebiliyor; geldiğinde liste o kullanıcıya
  // ait anahtardan yeniden okunuyor.
  useEffect(() => {
    setSonlar(sonlariOku(kullaniciId));
  }, [kullaniciId]);

  function sec(id: string, ad: string): void {
    sonlariYaz(kullaniciId, { id, ad });
    onSec(id, ad);
  }

  const suzulmus = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr");
    const hepsi = liste.data?.ogrenciler ?? [];
    if (q.length === 0) return hepsi;
    return hepsi.filter((o) =>
      `${o.ad} ${o.soyad} ${o.studentNo}`.toLocaleLowerCase("tr").includes(q),
    );
  }, [liste.data, arama]);

  return (
    <Kart baslik={baslik} aciklama={aciklama}>
      <div className="flex flex-col gap-3">
        {/*
          İŞ LİSTESİ YOLU ÜSTTE DURUYOR. Bekleyen işi olan öğrenciyi
          bulmanın en hızlı yolu o; seçici, o listede olmayan bir
          öğrenciye bakmak isteyen kişi için.
        */}
        {onIsListesi && (
          <div className="flex flex-wrap items-center gap-2">
            <Buton gorunum="ikincil" onClick={onIsListesi}>
              <Ikon ad="bugun" className="mr-2" />
              Bekleyen işlerden seç
            </Buton>
            <span className="text-notr-600 text-sm">ya da listeden bulun:</span>
          </div>
        )}

        {liste.isPending ? (
          <Iskelet satir={4} />
        ) : liste.isError ? (
          <BosDurum
            ton="hata"
            baslik="Öğrenci listesi alınamadı"
            aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
            eylem={
              <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
                Tekrar dene
              </Buton>
            }
          />
        ) : liste.data.ogrenciler.length === 0 ? (
          <BosDurum
            ikon="ogrenci"
            baslik="Kapsamınızda öğrenci yok"
            aciklama="Yalnızca size atanmış öğrenciler listelenir; bu, sistemde kayıt olmadığı anlamına gelmez."
          />
        ) : (
          <>
            {/*
              ================================================================
              SON KULLANILANLAR — F-STV-16
              ================================================================
              Arama, aradığı öğrencinin adını BİLEN kullanıcı için
              çalışıyor. Uzmanın günlük işi ise aynı birkaç öğrenci
              arasında gidip gelmek; her seferinde ad yazmak gereksiz
              sürtünme.

              KAPSAMA GÖRE SÜZÜLÜYOR. `localStorage` eskiyebilir: bir
              öğrenci uzmandan alınmış olabilir. Süzmeseydik ekran
              artık erişilemeyen bir öğrenciyi öneriyor, tıklayan da
              403 alıyordu — kendi ekranımızın ürettiği bir çıkmaz.

              ARAMA YAZILINCA GİZLENİYOR: arama zaten "bunu değil şunu
              arıyorum" demek. İki liste birden göstermek, aramanın
              sonucunu ikinci sıraya iter.
            */}
            {arama.trim().length === 0 &&
              (() => {
                const kapsamda = new Map(liste.data.ogrenciler.map((o) => [o.id, o]));
                const gorunen = sonlar.filter((s) => kapsamda.has(s.id));
                if (gorunen.length === 0) return null;
                return (
                  <div className="flex flex-col gap-2">
                    <p className="text-notr-600 text-xs font-medium tracking-wide uppercase">
                      Son kullanılanlar
                    </p>
                    <ul className="flex flex-col gap-2">
                      {gorunen.map((s) => {
                        const o = kapsamda.get(s.id)!;
                        return (
                          <li key={s.id}>
                            <SatirDugmesi onClick={() => sec(o.id, `${o.ad} ${o.soyad}`)}>
                              <Ikon ad="ogrenci" className="text-marka-600" />
                              {/*
                                Ad `localStorage`tan DEĞİL, taze listeden
                                okunuyor: öğrencinin adı düzeltilmiş
                                olabilir ve eski adı göstermek, uzmanın
                                yanlış kişiye baktığını sanmasına yol açar.
                              */}
                              <SatirMetni
                                baslik={`${o.ad} ${o.soyad}`}
                                altBaslik={`No ${o.studentNo}`}
                              />
                            </SatirDugmesi>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-notr-600 text-xs">
                      Bu liste yalnızca bu tarayıcıda tutuluyor.
                    </p>
                  </div>
                );
              })()}

            <Alan
              etiket="Ara"
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              placeholder="Ad veya öğrenci no"
            />

            {suzulmus.length === 0 ? (
              <p className="text-notr-600 text-sm">Aramaya uyan öğrenci yok.</p>
            ) : (
              <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto">
                {suzulmus.map((o) => (
                  <li key={o.id}>
                    <SatirDugmesi onClick={() => sec(o.id, `${o.ad} ${o.soyad}`)}>
                      <Ikon ad="ogrenci" className="text-notr-400" />
                      <SatirMetni baslik={`${o.ad} ${o.soyad}`} altBaslik={`No ${o.studentNo}`} />
                    </SatirDugmesi>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Kart>
  );
}

/**
 * ÖĞRENCİ SEÇİM ALANI — UNİKOÇ filtre kartındaki "Öğrenci" açılır listesi (sürüm 9a L2)
 *
 * Eski panelde öğrenciye bağlı ekranlar (Öğrenci Planları, Konu Takip, Haftalık
 * Program, Deneme Sınavları) ayrı bir "kimin?" sayfası AÇMIYORDU: ekranın kendisi
 * çiziliyor, öğrenci üstteki filtre kartında seçiliyordu. Bu alan o seçici.
 *
 * Kapsam SUNUCUDA: liste `GET /ogrenciler` (kişinin kapsamı). Son kullanılanlar
 * (F-STV-16) listenin başında ayrı grup olarak duruyor — tam liste seçicisindeki
 * özellik kaybolmasın; kapsamda olmayan eski kayıt süzülüyor (bkz. `OgrenciSecici`).
 *
 * Liste alınamazsa seçili öğrenci yine görünür (`seciliAd`): ekran öğrenciyi
 * bilmeye devam ediyor, yalnız değiştirme seçeneği eksik kalıyor.
 */
export function OgrenciSecimAlani({
  secili,
  seciliAd,
  onSec,
  etiket = "Öğrenci",
  yardim,
}: {
  secili: string | null;
  seciliAd?: string;
  onSec: (id: string, ad: string) => void;
  etiket?: string;
  yardim?: string;
}) {
  const liste = useQuery({ queryKey: ["ogrenciler"], queryFn: ogrenciListesi, retry: false });
  const ben = useQuery({ queryKey: ["ben"], queryFn: benGetir, retry: false });
  const kullaniciId = ben.data?.userId ?? null;
  const hepsi = liste.data?.ogrenciler ?? [];
  const kapsamda = new Map(hepsi.map((o) => [o.id, o]));
  const sonlar = sonlariOku(kullaniciId).filter((s) => kapsamda.has(s.id) && s.id !== secili);
  const adi = (o: OgrenciSatiri): string => `${o.ad} ${o.soyad}`;

  return (
    <Secim
      etiket={etiket}
      value={secili ?? ""}
      disabled={liste.isPending && !secili}
      yardim={
        liste.isError
          ? "Öğrenci listesi alınamadı; sayfayı yenileyin."
          : liste.isPending
            ? "Öğrenciler yükleniyor…"
            : hepsi.length === 0
              ? "Kapsamınızda öğrenci yok."
              : yardim
      }
      onChange={(e) => {
        const o = kapsamda.get(e.target.value);
        if (!o) return;
        sonlariYaz(kullaniciId, { id: o.id, ad: adi(o) });
        onSec(o.id, adi(o));
      }}
    >
      {!secili && <option value="">Öğrenci Seçin</option>}
      {secili && !kapsamda.has(secili) && (
        <option value={secili}>{seciliAd ?? "Seçili öğrenci"}</option>
      )}
      {sonlar.length > 0 && (
        <optgroup label="Son kullanılanlar">
          {sonlar.map((s) => {
            const o = kapsamda.get(s.id)!;
            return (
              <option key={`son-${o.id}`} value={o.id}>
                {adi(o)}
              </option>
            );
          })}
        </optgroup>
      )}
      {hepsi.length > 0 && (
        <optgroup label="Öğrencilerim">
          {hepsi.map((o) => (
            <option key={o.id} value={o.id}>
              {adi(o)} · No {o.studentNo}
            </option>
          ))}
        </optgroup>
      )}
    </Secim>
  );
}

/** Kapsamdaki öğrenciler — ekranın "seçili yoksa ilki" kararı için (aynı önbellek anahtarı). */
export function useOgrenciListesi() {
  return useQuery({ queryKey: ["ogrenciler"], queryFn: ogrenciListesi, retry: false });
}
